<template>
  <Teleport to="body">
    <div
      v-if="shield"
      class="ranked-call-shield"
      data-ranked-call
      :style="shieldStyle"
      @pointerdown.prevent.stop
      @pointerup.prevent.stop
      @click.prevent.stop
      @touchstart.prevent.stop
      @mousedown.prevent.stop
    />
  </Teleport>
  <Transition name="ranked-call">
    <aside
      v-if="offer"
      ref="root"
      class="ranked-call"
      data-ranked-call
      :class="{ rail }"
      :aria-label="t('duel.rankedCall', { name: offer.username })"
      @pointerdown.stop
      @touchstart.stop
      @mousedown.stop
      @click.stop
    >
      <div class="who">
        <RankBadge
          :league="offer.league"
          :division="offer.division"
          :placement-remaining="offer.placementRemaining"
          :size="rail ? 22 : 16"
        />
        <span class="who-name">{{ offer.username }}</span>
        <span class="who-level" :title="t('level.title')">{{ t('level.chip', { n: offer.level }) }}</span>
      </div>
      <p class="copy">{{ t('duel.rankedCallWait') }}</p>
      <div class="actions">
        <button
          ref="goBtn"
          type="button"
          class="go"
          :disabled="pending"
          :aria-label="coins > 0
            ? t('duel.rankedCallGoPay', { name: offer.username, n: coins })
            : t('duel.rankedCallGo')"
          :title="t('duel.rankedCallGoHint', { name: offer.username })"
          @pointerdown="armGo"
          @keydown="onGoKeydown"
        >
          {{ t('duel.rankedCallGo') }}
          <span v-if="coins > 0" class="pay">
            <CoinToken size="sm" />
            <span>×{{ coins }}</span>
          </span>
        </button>
        <button
          type="button"
          class="skip"
          @pointerdown="onSkipPointerDown"
          @click.stop="skip"
        >
          {{ t('duel.rankedCallIgnore') }}
        </button>
      </div>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { RANKED_CALL_COINS, RANKED_CALL_DAILY_CAP } from '@shared/index'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { useRankedLobbyStore } from '../stores/rankedLobby'
import { useI18n } from '../i18n/t'
import CoinToken from './CoinToken.vue'
import RankBadge from './RankBadge.vue'
import {
  bindRankedActivationGuard,
  blockRankedActivation,
  ignoreRankedCall,
  rankedActivationBlocked,
  rankedCallOffers,
  rankedCallSkip,
} from '../game/net/rankedCall'

const props = defineProps<{ pending?: boolean; rail?: boolean }>()

const emit = defineEmits<{
  duel: [id: string]
}>()

const { t } = useI18n()
const duelPrefs = useDuelPrefsStore()
const rankedLobby = useRankedLobbyStore()
const goBtn = ref<HTMLButtonElement | null>(null)
const root = ref<HTMLElement | null>(null)
const shield = ref(false)
const shieldStyle = ref<Record<string, string>>({})
let armedPointer: number | null = null
let lastTouchAt = 0
let removeArm: (() => void) | null = null
let unbindGuard: (() => void) | null = null
let shieldTimer: number | null = null

const offer = computed(() => {
  if (!duelPrefs.rankedPrompts) return null
  const skip = new Set(rankedCallSkip.value)
  return rankedCallOffers.value.find((row) => !skip.has(row.id)) ?? null
})

/** The coin stops at the daily cap. The note of who is waiting stays. */
const coins = computed(() =>
  rankedLobby.callClaims >= RANKED_CALL_DAILY_CAP ? 0 : RANKED_CALL_COINS,
)

onMounted(() => {
  unbindGuard = bindRankedActivationGuard()
})

onUnmounted(() => {
  clearArm()
  clearShield()
  unbindGuard?.()
  unbindGuard = null
})

function clearArm(): void {
  removeArm?.()
  removeArm = null
  armedPointer = null
}

function clearShield(): void {
  if (shieldTimer != null) {
    window.clearTimeout(shieldTimer)
    shieldTimer = null
  }
  shield.value = false
}

function commitGo(): void {
  if (rankedActivationBlocked()) return
  const id = offer.value?.id
  if (!id || props.pending) return
  emit('duel', id)
}

/**
 * A click that arrives after some other control closed is not a choice.
 * Duel only counts when the finger went down on this button.
 */
function armGo(event: PointerEvent): void {
  if (props.pending || rankedActivationBlocked()) return
  const now = performance.now()
  if (event.pointerType === 'touch') lastTouchAt = now
  if (event.pointerType === 'mouse' && now - lastTouchAt < 800) return
  clearArm()
  armedPointer = event.pointerId
  const end = (ev: PointerEvent) => {
    if (ev.pointerId !== armedPointer) return
    const btn = goBtn.value
    const inside = ev.target instanceof Node && !!btn?.contains(ev.target)
    clearArm()
    if (!inside || ev.type === 'pointercancel') return
    commitGo()
  }
  window.addEventListener('pointerup', end, true)
  window.addEventListener('pointercancel', end, true)
  removeArm = () => {
    window.removeEventListener('pointerup', end, true)
    window.removeEventListener('pointercancel', end, true)
  }
}

function onGoKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  commitGo()
}

function onSkipPointerDown(): void {
  clearArm()
  blockRankedActivation(800)
}

function skip(): void {
  const id = offer.value?.id
  if (!id) return
  const el = root.value
  if (el) {
    const rect = el.getBoundingClientRect()
    const pad = 24
    shieldStyle.value = {
      top: `${Math.max(0, rect.top - pad)}px`,
      left: `${Math.max(0, rect.left - pad)}px`,
      width: `${rect.width + pad * 2}px`,
      height: `${rect.height + pad * 2}px`,
    }
    shield.value = true
    if (shieldTimer != null) window.clearTimeout(shieldTimer)
    shieldTimer = window.setTimeout(() => {
      shieldTimer = null
      shield.value = false
    }, 800)
  }
  ignoreRankedCall(id)
}
</script>

<style scoped>
.ranked-call {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 176;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: min(240px, calc(100% - 120px));
  margin: 0;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid var(--md-sys-color-outline-variant);
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
  font-family: Roboto, system-ui, sans-serif;
  pointer-events: auto;
  transform: translate3d(0, 0, 0);
}

.ranked-call.rail {
  position: relative;
  top: auto;
  right: auto;
  width: 100%;
  z-index: 2;
}

.who {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
}

.who :deep(.rank-badge) {
  font-size: 10px;
}

.who :deep(.rank-roman) {
  font-size: 8px;
  line-height: 1.15;
  padding: 0 2px;
}

.ranked-call:not(.rail) .who {
  gap: 4px;
}

.ranked-call:not(.rail) .who-name {
  font-size: 12px;
  line-height: 16px;
}

.ranked-call:not(.rail) .who-level {
  font-size: 11px;
}

.ranked-call:not(.rail) .copy {
  font-size: 12px;
  line-height: 16px;
}

.who-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.who-level {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--md-sys-color-on-surface-variant);
}

.copy {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
}

.actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  width: 100%;
}

.go,
.skip {
  min-height: 32px;
  padding: 0 10px;
  border-radius: 8px;
  border: none;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  touch-action: manipulation;
}

.go {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.pay {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 700;
}

.skip {
  width: 100%;
  background: transparent;
  color: var(--md-sys-color-on-surface-variant);
}

.go:disabled {
  opacity: 0.6;
  cursor: default;
}

.ranked-call-enter-active,
.ranked-call-leave-active {
  transition:
    opacity var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard);
}

.ranked-call-leave-active {
  pointer-events: none;
}

.ranked-call-enter-from,
.ranked-call-leave-to {
  opacity: 0;
  transform: translate3d(0, -6px, 0);
}

.ranked-call-shield {
  position: fixed;
  z-index: 400;
  pointer-events: auto;
  background: transparent;
}

@media (prefers-reduced-motion: reduce) {
  .ranked-call-enter-active,
  .ranked-call-leave-active {
    transition: none;
  }
}
</style>
